import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { isOwnerOnly, isRefused, isSignedOut, signOut } from "./api";
import { AppPage } from "./app-nav";
import { LoginScreen } from "./login";

/**
 * Ce qu'un ecran montre quand sa lecture a echoue : l'ecran de connexion sans session (401), un
 * refus poli si l'adresse connectee n'est pas admise (403), la page du proprietaire seul ouverte
 * par un proche (403 `owner_only`), sinon la panne avec « Réessayer ».
 * La garde du serveur protege les donnees ; ceci ne fait que choisir l'ecran.
 */
export function SessionGate({ error, retry }: { error: unknown; retry: () => void }) {
  if (isSignedOut(error)) return <LoginScreen />;
  if (isRefused(error)) return <Refused />;
  if (isOwnerOnly(error)) return <OwnerOnly />;
  return (
    <section className="flex flex-col items-start gap-3">
      <p className="m-0 text-destructive" role="alert">
        Ta frise ne répond pas pour le moment. Réessaie dans un instant.
      </p>
      <button
        className="min-h-11 rounded-[10px] border border-input px-4 text-sm"
        onClick={retry}
        type="button"
      >
        Réessayer
      </button>
    </section>
  );
}

/** Connecte, mais avec une adresse qui n'a pas acces : on le dit, et on propose d'en changer. */
function Refused() {
  const queryClient = useQueryClient();
  const [leaving, setLeaving] = useState(false);
  return (
    <section className="mx-auto flex w-full max-w-md flex-col gap-4 py-6">
      <h1 className="tl-display m-0 text-[34px]">Ma timeline</h1>
      <p className="m-0 text-base text-[#C9D0DE]" role="alert">
        Cette adresse n'a pas accès à cette frise. Connecte-toi avec l'adresse invitée.
      </p>
      <button
        className="min-h-12 rounded-[10px] bg-primary text-base font-semibold text-primary-foreground disabled:opacity-60"
        disabled={leaving}
        onClick={() => {
          setLeaving(true);
          void signOut()
            .catch(() => undefined)
            .finally(() => {
              setLeaving(false);
              void queryClient.invalidateQueries({ queryKey: ["t"] });
            });
        }}
        type="button"
      >
        Changer d'adresse
      </button>
    </section>
  );
}

/** Un proche sur une page du proprietaire seul : on le dit, et on le ramene a la frise. */
function OwnerOnly() {
  return (
    <AppPage viewerRole="family">
      <section className="flex flex-col items-start gap-3">
        <p className="m-0 text-base text-[#C9D0DE]" role="alert">
          Cette page est réservée au propriétaire de la frise.
        </p>
        <Link className="inline-flex min-h-11 items-center text-tl-accent underline" to="/">
          Revenir à la frise
        </Link>
      </section>
    </AppPage>
  );
}
